import { ReceiveItem, ReceiveItemBatch } from 'src/features/transfers/TransfersTypes'
import { handleOnKeyDown } from 'src/utils/Form'
import * as S from './ModalUnfitGondola.styles'
import { useForm } from 'react-hook-form'
import { InputAdornment, Typography } from '@mui/material'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import { useEffect, useState } from 'react'
import { cloneDeep } from 'lodash'
import ReceiveItemBatchesTable from '../ReceiveItemBatchesTable/ReceiveItemBatchesTable'
import { useDispatch } from 'react-redux'
import { actions } from '../../../TransfersState'
import { toast } from 'react-toastify'

interface ModalUnfitGondolaProps {
  badge: string
  show: boolean
  closeModal: () => void
  item: ReceiveItem
  startedAt: Date
}

interface FormData {
  boxesQuantity: number
  boxQuantity: number
  singleQuantity: number
  totalQuantity: number
}

const ModalUnfitGondola = ({
  badge,
  show,
  closeModal,
  item,
  startedAt,
}: ModalUnfitGondolaProps) => {
  const {
    register,
    watch,
    getValues,
    setValue,
    formState: { errors },
    setError,
    trigger,
    clearErrors,
    reset,
  } = useForm<FormData>()

  const [leftBatches, setLeftBatches] = useState<ReceiveItemBatch[]>()
  const hasLeftBatches = leftBatches && leftBatches.length > 0

  const [showModalUnfitGondolaConfirmation, setShowModalUnfitGondolaConfirmation] = useState(false)

  const [preventDoubleClick, setPreventDoubleClick] = useState(false)

  const totalBatchesQuantity = item.batches.reduce((acc, batch) => acc + batch.quantity, 0)

  const dispatch = useDispatch()

  useEffect(() => {
    reset()
    setLeftBatches([])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  useEffect(() => {
    const boxesQuantity = Number(getValues('boxesQuantity'))
    const boxQuantity = Number(getValues('boxQuantity'))
    const singleQuantity = Number(getValues('singleQuantity'))
    const total = (boxQuantity * boxesQuantity) + singleQuantity
    setValue('totalQuantity', Number.isNaN(total) ? 0 : total)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('boxesQuantity'), watch('boxQuantity'), watch('singleQuantity')])

  const removeMovementedBatchesQuantities = (_batches: ReceiveItemBatch[], _quantityToRemove: number): ReceiveItemBatch[] => {
    const clonedBatches = cloneDeep(_batches)
    let quantityToRemove = _quantityToRemove
    clonedBatches.forEach((batch) => {
      if (batch.quantity >= quantityToRemove) {
        batch.quantity -= quantityToRemove
        quantityToRemove = 0
      } else {
        quantityToRemove -= batch.quantity
        batch.quantity = 0
      }
    })
    return clonedBatches.filter((batch) => batch.quantity > 0)
  }

  const onReject = () => {
    if (!hasLeftBatches) {
      closeModal()
    } else {
      setLeftBatches([])
    }
  }

  const onConfirm = async () => {
    if (!hasLeftBatches) {
      clearErrors()

      const validationBoxesQuantity = await trigger('boxesQuantity')
      const validationBoxQuantity = await trigger('boxQuantity')
      const validationSingleQuantity = await trigger('singleQuantity')

      if (!validationBoxesQuantity || !validationBoxQuantity || !validationSingleQuantity) {
        return
      }

      if (getValues('totalQuantity') > totalBatchesQuantity) {
        setError('totalQuantity', { message: 'Deve ser menor ou igual a quantidade total de unidades' })
        return
      }

      if (getValues('totalQuantity') < 1) {
        setError('totalQuantity', { message: 'Deve ser maior que 0 (zero)' })
      }

      setLeftBatches(removeMovementedBatchesQuantities(item.batches, totalBatchesQuantity - getValues('totalQuantity')))
    } else {
      setShowModalUnfitGondolaConfirmation(true)
    }
  }

  const onConfirmUnfitGondola = () => {
    if (leftBatches === undefined || leftBatches.length === 0) {
      toast.error('Não há lotes sobrando para utilizar a função não coube.')
      return
    }

    if (preventDoubleClick) {
      return
    }

    setPreventDoubleClick(true)

    const onSuccess = () => {
      setPreventDoubleClick(false)
      closeModal()
      if (totalBatchesQuantity === getValues('totalQuantity')) {
        toast.info('Produto definido para outra localização!')
      } else {
        toast.info('Parte do produto movimentada para gôndola, unidades restantes definidas para uma nova localização!')
      }
    }

    const onError = () => {
      setPreventDoubleClick(false)
    }

    dispatch(actions.receiveUnfitMovement({
      badge,
      item: { ...item, batches: leftBatches },
      startedAt,
      onSuccess,
      onError,
    }))
  }

  return (
    <>
      <ConfirmOrRejectModal
        show={show}
        title='Direto Gôndola'
        dataTestId='modal-unfit-gondola-test-id'
        zIndex={showModalUnfitGondolaConfirmation ? 520 : 1040}
        customizableDescription={(
          <S.Container>
            {!hasLeftBatches ? (
              <>
                <Typography fontWeight='bold'>Informe a quantidade que não coube</Typography>
                <S.Form>
                  <S.CustomTextField
                    onKeyDown={(e) => handleOnKeyDown(e, 'boxQuantity')}
                    name='boxesQuantity'
                    id='boxesQuantity'
                    label='Caixas'
                    type='number'
                    autoFocus
                    error={!!errors?.boxesQuantity}
                    helperText={errors?.boxesQuantity?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position='end'
                          sx={{ cursor: 'pointer' }}
                          data-testid='input-adornment-test-id'
                        >
                          UN
                        </InputAdornment>
                      ),
                      inputProps: {
                        ...register('boxesQuantity',
                          {
                            required: 'Campo obrigatório',
                            min: { value: 0, message: 'Deve ser zero ou um valor positivo' },
                            pattern: { value: new RegExp('^[0-9]*$'), message: 'Deve ser um número inteiro' },
                          }),
                        'data-testid': 'boxes-quantity-test-id',
                      },
                    }}
                  />
                  <S.CustomTextField
                    onKeyDown={(e) => handleOnKeyDown(e, 'singleQuantity')}
                    name='boxQuantity'
                    id='boxQuantity'
                    label='Caixaria'
                    type='number'
                    error={!!errors?.boxQuantity}
                    helperText={errors?.boxQuantity?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position='end'
                          sx={{ cursor: 'pointer' }}
                          data-testid='input-adornment-test-id'
                        >
                          UN
                        </InputAdornment>
                      ),
                      inputProps: {
                        ...register('boxQuantity',
                          {
                            required: 'Campo obrigatório',
                            min: { value: 0, message: 'Deve ser zero ou um valor positivo' },
                            pattern: { value: new RegExp('^[0-9]*$'), message: 'Deve ser um número inteiro' },
                          }),
                        'data-testid': 'box-quantity-test-id',
                      },
                    }}
                  />
                  <S.CustomTextField
                    onKeyDown={(e) => handleOnKeyDown(e, 'boxesQuantity')}
                    name='singleQuantity'
                    id='singleQuantity'
                    label='Avulso'
                    type='number'
                    error={!!errors?.singleQuantity}
                    helperText={errors?.singleQuantity?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position='end'
                          sx={{ cursor: 'pointer' }}
                          data-testid='input-adornment-test-id'
                        >
                          UN
                        </InputAdornment>
                      ),
                      inputProps: {
                        ...register('singleQuantity',
                          {
                            required: 'Campo obrigatório',
                            min: { value: 0, message: 'Deve ser zero ou um valor positivo' },
                            pattern: { value: new RegExp('^[0-9]*$'), message: 'Deve ser um número inteiro' },
                          }),
                        'data-testid': 'single-quantity-test-id',
                      },
                    }}
                  />
                  <S.CustomTextField
                    onKeyDown={() => null}
                    name='totalQuantity'
                    id='totalQuantity'
                    label='Total'
                    type='number'
                    error={!!errors?.totalQuantity}
                    helperText={errors?.totalQuantity?.message}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position='end'
                          sx={{ cursor: 'pointer' }}
                          data-testid='input-adornment-test-id'
                        >
                          UN
                        </InputAdornment>
                      ),
                      inputProps: {
                        ...register('totalQuantity'),
                        'data-testid': 'total-quantity-test-id',
                      },
                    }}
                    disabled
                  />
                </S.Form>
              </>
            ) : (
              <>
                <Typography fontWeight='bold'>
                  {leftBatches.length === 1
                    ? 'Confirme a validade da caixa que sobrou'
                    : 'Confirme as validades das caixas que sobraram'}
                </Typography>
                <ReceiveItemBatchesTable batches={leftBatches} />
              </>
            )}
          </S.Container>
        )}
        onClose={closeModal}
        onReject={onReject}
        rejectButtonText={!hasLeftBatches ? 'Cancelar' : 'Voltar'}
        onConfirm={onConfirm}
        confirmButtonText='Confirmar'
      />
      <ConfirmOrRejectModal
        show={showModalUnfitGondolaConfirmation}
        title='Direto Gôndola'
        dataTestId='modal-unfit-gondola-confirmation-test-id'
        customizableDescription={(
          <S.ConfirmModalContainer>
            <S.ConfirmModalText>
              Ao confirmar a ação, você garante que as respectivas validades e quantidades restantes foram checadas. As unidades restantes deverão ser movidas para uma nova posição.
            </S.ConfirmModalText>
            <S.ConfirmModalConfirmationText>
              Deseja prosseguir?
            </S.ConfirmModalConfirmationText>
          </S.ConfirmModalContainer>
        )}
        onClose={() => !preventDoubleClick && setShowModalUnfitGondolaConfirmation(false)}
        onReject={() => !preventDoubleClick && setShowModalUnfitGondolaConfirmation(false)}
        onConfirm={onConfirmUnfitGondola}
      />
    </>
  )
}

export default ModalUnfitGondola
